import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';

const Container = styled.div`
  padding: 2rem;
  background-color: #f5f5f5;
  min-height: 100vh;
`;

const Breadcrumb = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #ddd;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  color: #333;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  background-color: ${props => props.primary ? '#007bff' : props.danger ? '#dc3545' : props.success ? '#28a745' : '#6c757d'};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background-color: ${props => props.primary ? '#0069d9' : props.danger ? '#c82333' : props.success ? '#218838' : '#5a6268'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid ${props => props.active ? '#007bff' : 'transparent'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.color || '#333'};
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-top: 0.25rem;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterSelect = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  font-size: 0.9rem;
  min-width: 120px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const SearchInput = styled.input`
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  font-size: 0.9rem;
  flex: 1;
  max-width: 300px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const DateInput = styled.input`
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const BatchActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding: 1rem;
  background: #e7f3ff;
  border-radius: 8px;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const SelectedCount = styled.span`
  font-weight: 500;
  color: #007bff;
  margin-right: 1rem;
`;

const DemandList = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const DemandItem = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  background: ${props => props.selected ? '#e7f3ff' : 'white'};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${props => props.selected ? '#e7f3ff' : '#f9f9f9'};
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  margin-top: 0.25rem;
`;

const DemandContent = styled.div`
  flex: 1;
`;

const DemandHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
`;

const DemandTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 0;
  flex: 1;
`;

const Badge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${props => {
    switch(props.type) {
      case 'pending': return '#fff3cd';
      case 'processing': return '#cce7ff';
      case 'completed': return '#d4edda';
      case 'cancelled': return '#f8d7da';
      default: return '#e9ecef';
    }
  }};
  color: ${props => {
    switch(props.type) {
      case 'pending': return '#856404';
      case 'processing': return '#004085';
      case 'completed': return '#155724';
      case 'cancelled': return '#721c24';
      default: return '#495057';
    }
  }};
  margin-left: 1rem;
`;

const TypeBadge = styled.span`
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  background-color: #e9ecef;
  color: #495057;
  margin-left: 0.5rem;
`;

const PriorityBadge = styled.span`
  color: ${props => props.priority === 'high' ? '#dc3545' : props.priority === 'medium' ? '#ffc107' : '#28a745'};
  font-weight: 600;
  margin-left: 0.5rem;
`;

const DemandDescription = styled.p`
  color: #666;
  margin-bottom: 0.75rem;
  line-height: 1.4;
`;

const DemandMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
  color: #888;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const DemandUser = styled.span`
  font-weight: 500;
`;

const DemandActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  padding: 0.375rem 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  color: #333;
  cursor: pointer;
  font-size: 0.8rem;

  &:hover {
    background-color: #f8f9fa;
  }

  &.primary {
    background-color: #007bff;
    color: white;
    border-color: #007bff;

    &:hover {
      background-color: #0069d9;
    }
  }

  &.success {
    background-color: #28a745;
    color: white;
    border-color: #28a745;

    &:hover {
      background-color: #218838;
    }
  }

  &.danger {
    background-color: #dc3545;
    color: white;
    border-color: #dc3545;

    &:hover {
      background-color: #c82333;
    }
  }

  &.warning {
    background-color: #ffc107;
    color: #333;
    border-color: #ffc107;

    &:hover {
      background-color: #e0a800;
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #888;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #666;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #dc3545;
`;

const Toast = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 1rem 1.5rem;
  background: ${props => props.type === 'success' ? '#28a745' : props.type === 'error' ? '#dc3545' : '#007bff'};
  color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: ${props => props.size === 'large' ? '900px' : '600px'};
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }
`;

const ModalBody = styled.div`
  margin-bottom: 1.5rem;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const ReplyList = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
`;

const ReplyItem = styled.div`
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const ReplyHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
`;

const ReplyAdmin = styled.span`
  font-weight: 500;
  color: #007bff;
`;

const ReplyTime = styled.span`
  color: #888;
`;

const ReplyContent = styled.p`
  margin: 0;
  color: #333;
  line-height: 1.5;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const PageButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover:not(:disabled) {
    background: #f8f9fa;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.active {
    background: #007bff;
    color: white;
    border-color: #007bff;
  }
`;

const SelectAllContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
`;

const ConfirmModalContent = styled(ModalContent)`
  max-width: 500px;
`;

const WarningIcon = styled.div`
  font-size: 3rem;
  text-align: center;
  margin-bottom: 1rem;
`;

const ConfirmMessage = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
  font-size: 1rem;
  color: #333;
`;

const ConfirmDetails = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  max-height: 200px;
  overflow-y: auto;
`;

const ConfirmDetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #eee;
  font-size: 0.9rem;

  &:last-child {
    border-bottom: none;
  }
`;

const DeleteProgress = styled.div`
  margin-top: 1rem;
`;

const ProgressBar = styled.div`
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #dc3545 0%, #c82333 100%);
  transition: width 0.3s ease;
  width: ${props => props.percent}%;
`;

const ProgressText = styled.div`
  text-align: center;
  font-size: 0.875rem;
  color: #666;
`;

const SelectionDropdown = styled.select`
  padding: 0.375rem 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  font-size: 0.85rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const DemandsPage = () => {
  const navigate = useNavigate();
  const [demands, setDemands] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    processing: 0,
    completed: 0,
    cancelled: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    type: '',
    search: '',
    startDate: '',
    endDate: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0
  });
  const [selectedDemands, setSelectedDemands] = useState([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [showBatchDeleteModal, setShowBatchDeleteModal] = useState(false);
  const [batchDeleteProgress, setBatchDeleteProgress] = useState({ current: 0, total: 0, deleting: false });
  const [selectedDemand, setSelectedDemand] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [submittingReward, setSubmittingReward] = useState(false);
  const [rewardForm, setRewardForm] = useState({
    commissionBasis: 'sales',
    dealAmount: '',
    profitAmount: '',
    rewardRate: '',
    rewardStatus: 'dealed',
    rewardRemark: ''
  });
  // 登记需求：字段与小程序录入表单共用同一份配置
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [submittingCreate, setSubmittingCreate] = useState(false);
  const [typeOptions, setTypeOptions] = useState([]);
  const [commissionOptions, setCommissionOptions] = useState([]);
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    type: 'project',
    priority: 'medium',
    details: {},
    commissionBasis: 'sales',
    expectedCommissionRate: 5,
    budget: '',
    contact: ''
  });
  const [selectFilter, setSelectFilter] = useState('all'); // 用于批量操作的选择性筛选

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const fetchDemands = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.type) params.append('type', filters.type);
      if (filters.search) params.append('search', filters.search);
      params.append('page', pagination.page);
      params.append('pageSize', pagination.pageSize);

      const response = await axios.get(`/api/admin/demands?${params.toString()}`);

      setDemands(response.data.demands || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total || 0,
        totalPages: response.data.totalPages || 0
      }));
    } catch (err) {
      console.error('获取需求列表失败:', err);
      setError('获取需求列表失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/admin/demands/stats');
      setStats(response.data.stats || {
        total: 0,
        pending: 0,
        processing: 0,
        completed: 0,
        cancelled: 0
      });
    } catch (err) {
      console.error('获取需求统计失败:', err);
    }
  };

  // 需求分类与分润依据配置：由后端下发，与小程序录入表单共用
  const fetchDemandTypes = async () => {
    try {
      const res = await axios.get('/api/demands/types');
      setTypeOptions(res.data.types || []);
      setCommissionOptions((res.data.commission && res.data.commission.basis) || []);
    } catch (err) {
      console.error('获取需求表单配置失败:', err);
    }
  };

  useEffect(() => {
    fetchDemands();
    fetchStats();
  }, [filters, pagination.page]);

  useEffect(() => {
    fetchDemandTypes();
  }, []);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedDemands(demands.map(d => d._id));
    } else {
      setSelectedDemands([]);
    }
  };

  const handleSelect = (id) => {
    if (selectedDemands.includes(id)) {
      setSelectedDemands(selectedDemands.filter(i => i !== id));
    } else {
      setSelectedDemands([...selectedDemands, id]);
    }
  };

  const handleBatchStatusUpdate = async (status) => {
    if (selectedDemands.length === 0) {
      showToast('请先选择要更新的需求', 'error');
      return;
    }

    try {
      const response = await axios.put('/api/admin/demands/batch-status', {
        ids: selectedDemands,
        status
      });
      showToast(response.data.message);
      setSelectedDemands([]);
      fetchDemands();
      fetchStats();
    } catch (err) {
      console.error('批量更新状态失败:', err);
      showToast('批量更新失败', 'error');
    }
  };

  const handleDelete = async (demandId) => {
    if (!window.confirm('确定要删除这条需求吗？此操作不可撤销。')) {
      return;
    }

    try {
      await axios.delete(`/api/admin/demands/${demandId}`);
      showToast('需求删除成功');
      fetchDemands();
      fetchStats();
    } catch (err) {
      console.error('删除需求失败:', err);
      showToast('删除失败', 'error');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedDemands.length === 0) {
      showToast('请先选择要删除的需求', 'error');
      return;
    }

    setShowBatchDeleteModal(true);
    setBatchDeleteProgress({ current: 0, total: selectedDemands.length, deleting: false });
  };

  const confirmBatchDelete = async () => {
    if (selectedDemands.length === 0) return;

    setBatchDeleteProgress({ current: 0, total: selectedDemands.length, deleting: true });

    try {
      // 分批删除，每批10条
      const batchSize = 10;
      let deletedCount = 0;
      const totalBatches = Math.ceil(selectedDemands.length / batchSize);

      for (let i = 0; i < totalBatches; i++) {
        const batch = selectedDemands.slice(i * batchSize, (i + 1) * batchSize);
        
        const response = await axios.post('/api/admin/demands/batch-delete', {
          ids: batch
        });
        
        deletedCount += response.data.deletedCount || batch.length;
        setBatchDeleteProgress({ 
          current: deletedCount, 
          total: selectedDemands.length, 
          deleting: true 
        });
      }

      setShowBatchDeleteModal(false);
      showToast(`成功删除 ${deletedCount} 条需求`);
      setSelectedDemands([]);
      setSelectFilter('all');
      fetchDemands();
      fetchStats();
    } catch (err) {
      console.error('批量删除失败:', err);
      showToast(err.response?.data?.message || '批量删除失败', 'error');
      setShowBatchDeleteModal(false);
    }
  };

  // 快捷键支持
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + A: 全选当前页
      if ((e.ctrlKey || e.metaKey) && e.key === 'a' && !['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) {
        e.preventDefault();
        setSelectedDemands(demands.map(d => d._id));
      }
      // Delete: 批量删除选中项
      if (e.key === 'Delete' && selectedDemands.length > 0 && !['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) {
        e.preventDefault();
        handleBatchDelete();
      }
      // Escape: 取消选择
      if (e.key === 'Escape' && selectedDemands.length > 0) {
        setSelectedDemands([]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [demands, selectedDemands]);

  // 按状态筛选选中项
  const handleSelectByStatus = (status) => {
    if (status === 'all') {
      setSelectedDemands(demands.map(d => d._id));
    } else {
      setSelectedDemands(demands.filter(d => d.status === status).map(d => d._id));
    }
    setSelectFilter(status);
  };

  const handleGenerateTestData = async () => {
    try {
      const response = await axios.post('/api/admin/demands/seed');
      if (response.data.success) {
        showToast(response.data.message);
        fetchDemands();
        fetchStats();
      } else {
        showToast(response.data.message, 'error');
      }
    } catch (err) {
      console.error('生成测试数据失败:', err);
      showToast('生成测试数据失败', 'error');
    }
  };

  const handleClearDemoData = async () => {
    if (!window.confirm('确定要清空所有演示数据吗？')) return;
    try {
      const response = await axios.delete('/api/admin/demands/demo');
      if (response.data.success) {
        showToast(response.data.message);
        fetchDemands();
        fetchStats();
      } else {
        showToast(response.data.message, 'error');
      }
    } catch (err) {
      console.error('清空演示数据失败:', err);
      showToast('清空演示数据失败', 'error');
    }
  };

  const handleExportCSV = () => {
    const headers = ['标题', '描述', '类型', '优先级', '状态', '用户', '邮箱', '提交时间', '更新时间'];
    const csvData = demands.map(d => [
      d.title,
      d.description.replace(/"/g, '""'),
      getTypeText(d.type),
      getPriorityText(d.priority),
      getStatusText(d.status),
      d.userId?.username || (d.createdByAdmin ? '后台登记' : '未知'),
      d.userId?.email || '',
      new Date(d.createdAt).toLocaleString(),
      new Date(d.updatedAt).toLocaleString()
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `demands_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    showToast('导出成功');
  };

  const handleStatusUpdate = async (demandId, status) => {
    try {
      await axios.put(`/api/admin/demands/${demandId}/status`, { status });
      showToast('状态更新成功');
      fetchDemands();
      fetchStats();
    } catch (err) {
      console.error('更新需求状态失败:', err);
      showToast('更新失败', 'error');
    }
  };

  const handleViewDetail = async (demand) => {
    try {
      const response = await axios.get(`/api/admin/demands/${demand._id}`);
      setSelectedDemand(response.data.demand);
      setShowDetailModal(true);
    } catch (err) {
      console.error('获取需求详情失败:', err);
      showToast('获取详情失败', 'error');
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim()) {
      showToast('请输入回复内容', 'error');
      return;
    }

    setSubmittingReply(true);
    try {
      await axios.post(`/api/admin/demands/${selectedDemand._id}/reply`, {
        reply: replyContent
      });
      setReplyContent('');
      setShowReplyModal(false);
      showToast('回复发送成功');
      fetchDemands();
      fetchStats();
    } catch (err) {
      console.error('回复需求失败:', err);
      showToast('回复失败', 'error');
    } finally {
      setSubmittingReply(false);
    }
  };

  // 打开奖励弹窗：奖励比例默认取用户录入时预设的期望分润值
  const openRewardModal = (demand) => {
    setSelectedDemand(demand);
    setRewardForm({
      commissionBasis: demand.commissionBasis === 'profit' ? 'profit' : 'sales',
      dealAmount: demand.dealAmount ? String(demand.dealAmount) : '',
      profitAmount: demand.profitAmount ? String(demand.profitAmount) : '',
      rewardRate: String(demand.rewardRate || demand.expectedCommissionRate || 0),
      rewardStatus: demand.rewardStatus && demand.rewardStatus !== 'none' ? demand.rewardStatus : 'dealed',
      rewardRemark: demand.rewardRemark || ''
    });
    setShowRewardModal(true);
  };

  const handleRewardSubmit = async () => {
    const dealAmount = Number(rewardForm.dealAmount);
    const rewardRate = Number(rewardForm.rewardRate);

    if (!Number.isFinite(dealAmount) || dealAmount < 0) {
      showToast('请填写有效的成交金额', 'error');
      return;
    }
    if (!Number.isFinite(rewardRate) || rewardRate < 0 || rewardRate > 100) {
      showToast('奖励比例需在 0-100 之间', 'error');
      return;
    }

    // 按利润分润时必须给出利润金额
    const profitAmount = Number(rewardForm.profitAmount);
    if (rewardForm.commissionBasis === 'profit' && (!Number.isFinite(profitAmount) || profitAmount <= 0)) {
      showToast('按利润分润需填写利润金额', 'error');
      return;
    }

    setSubmittingReward(true);
    try {
      await axios.put(`/api/admin/demands/${selectedDemand._id}/reward`, {
        commissionBasis: rewardForm.commissionBasis,
        dealAmount,
        profitAmount: rewardForm.commissionBasis === 'profit' ? profitAmount : 0,
        rewardRate,
        rewardStatus: rewardForm.rewardStatus,
        rewardRemark: rewardForm.rewardRemark
      });
      showToast('奖励信息已保存');
      setShowRewardModal(false);
      fetchDemands();
      fetchStats();
    } catch (err) {
      console.error('保存奖励信息失败:', err);
      showToast(err.response?.data?.message || '保存失败', 'error');
    } finally {
      setSubmittingReward(false);
    }
  };

  // 当前选中分类的字段与说明（与小程序一致的动态表单）
  const currentTypeConfig = typeOptions.find(t => t.value === createForm.type) || null;
  const currentTypeFields = (currentTypeConfig && currentTypeConfig.fields) || [];
  const currentTypeDesc = (currentTypeConfig && currentTypeConfig.desc) || '';

  const openCreateModal = () => {
    setCreateForm({
      title: '',
      description: '',
      type: (typeOptions[0] && typeOptions[0].value) || 'project',
      priority: 'medium',
      details: {},
      commissionBasis: 'sales',
      expectedCommissionRate: 5,
      budget: '',
      contact: ''
    });
    if (typeOptions.length === 0) fetchDemandTypes();
    setShowCreateModal(true);
  };

  const handleCreateFieldChange = (key, value) => {
    setCreateForm(prev => ({
      ...prev,
      details: { ...prev.details, [key]: value }
    }));
  };

  const handleCreateDemand = async () => {
    if (!createForm.title.trim()) {
      showToast('请填写需求标题', 'error');
      return;
    }
    if (!createForm.description.trim()) {
      showToast('请填写需求描述', 'error');
      return;
    }

    // 分类专属必填项（与小程序一致）
    const missing = currentTypeFields
      .filter(f => f.required)
      .filter(f => {
        const v = createForm.details[f.key];
        return v === undefined || v === null || String(v).trim() === '';
      });
    if (missing.length > 0) {
      showToast('请填写：' + missing.map(f => f.label).join('、'), 'error');
      return;
    }

    const rate = Number(createForm.expectedCommissionRate);
    if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
      showToast('期望分润比例需在 0-100 之间', 'error');
      return;
    }

    setSubmittingCreate(true);
    try {
      await axios.post('/api/admin/demands', {
        title: createForm.title.trim(),
        description: createForm.description.trim(),
        type: createForm.type,
        priority: createForm.priority,
        details: createForm.details,
        commissionBasis: createForm.commissionBasis,
        expectedCommissionRate: rate,
        budget: Number(createForm.budget) || 0,
        contact: createForm.contact.trim()
      });
      showToast('需求登记成功');
      setShowCreateModal(false);
      fetchDemands();
      fetchStats();
    } catch (err) {
      console.error('登记需求失败:', err);
      showToast(err.response?.data?.message || '登记失败', 'error');
    } finally {
      setSubmittingCreate(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    handleFilterChange('search', e.target.search.value);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: '待处理',
      processing: '处理中',
      completed: '已完成',
      cancelled: '已取消'
    };
    return statusMap[status] || status;
  };

  const getTypeText = (type) => {
    const typeMap = {
      project: '项目需求',
      purchase: '产品采购',
      service: '技术服务'
    };
    return typeMap[type] || type;
  };

  // 分类专属字段的中文标签（详情展示用）
  const DETAIL_LABELS = {
    duration: '周期',
    location: '地点',
    industry: '行业',
    startTime: '期望启动',
    scale: '规模',
    category: '采购品类',
    quantity: '采购数量',
    brand: '品牌要求',
    deliveryLocation: '交付地点',
    deliveryDeadline: '期望到货',
    serviceType: '服务类型',
    mode: '服务方式',
    environment: '现有系统/环境'
  };

  const getPriorityText = (priority) => {
    const priorityMap = {
      low: '低',
      medium: '中',
      high: '高'
    };
    return priorityMap[priority] || priority;
  };

  const allSelected = demands.length > 0 && selectedDemands.length === demands.length;

  if (loading && demands.length === 0) {
    return (
      <Container>
        <Header>
          <Title>需求管理</Title>
        </Header>
        <LoadingState>加载中...</LoadingState>
        {toast && <Toast type={toast.type}>{toast.message}</Toast>}
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Header>
          <Title>需求管理</Title>
        </Header>
        <ErrorState>{error}</ErrorState>
        <Button onClick={fetchDemands}>重试</Button>
        {toast && <Toast type={toast.type}>{toast.message}</Toast>}
      </Container>
    );
  }

  return (
    <AdminLayout title="需求管理">
      <Container>
        {toast && <Toast type={toast.type}>{toast.message}</Toast>}

        <Breadcrumb>
          <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }}>仪表盘</a> / 
            <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/users'); }}>用户管理</a> / 需求管理
          </Breadcrumb>

          <Header>
            <Title>需求管理</Title>
            <HeaderActions>
              <Button primary onClick={openCreateModal}>登记需求</Button>
              <Button success onClick={handleGenerateTestData}>生成测试数据</Button>
              <Button danger onClick={handleClearDemoData}>清空演示数据</Button>
              <Button onClick={handleExportCSV}>导出CSV</Button>
              <Button onClick={() => { fetchDemands(); fetchStats(); }}>刷新</Button>
            </HeaderActions>
          </Header>

      <StatsContainer>
        <StatCard onClick={() => handleFilterChange('status', '')} active={filters.status === ''}>
          <StatValue>{stats.total}</StatValue>
          <StatLabel>全部需求</StatLabel>
        </StatCard>
        <StatCard onClick={() => handleFilterChange('status', 'pending')} active={filters.status === 'pending'}>
          <StatValue style={{ color: '#ffc107' }}>{stats.pending}</StatValue>
          <StatLabel>待处理</StatLabel>
        </StatCard>
        <StatCard onClick={() => handleFilterChange('status', 'processing')} active={filters.status === 'processing'}>
          <StatValue style={{ color: '#007bff' }}>{stats.processing}</StatValue>
          <StatLabel>处理中</StatLabel>
        </StatCard>
        <StatCard onClick={() => handleFilterChange('status', 'completed')} active={filters.status === 'completed'}>
          <StatValue style={{ color: '#28a745' }}>{stats.completed}</StatValue>
          <StatLabel>已完成</StatLabel>
        </StatCard>
        <StatCard onClick={() => handleFilterChange('status', 'cancelled')} active={filters.status === 'cancelled'}>
          <StatValue style={{ color: '#dc3545' }}>{stats.cancelled}</StatValue>
          <StatLabel>已取消</StatLabel>
        </StatCard>
      </StatsContainer>

      <FilterContainer>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0.5rem', flex: 1 }}>
          <SearchInput
            type="text"
            name="search"
            placeholder="搜索需求标题或描述..."
            defaultValue={filters.search}
          />
          <Button type="submit">搜索</Button>
        </form>
        <DateInput
          type="date"
          value={filters.startDate}
          onChange={(e) => handleFilterChange('startDate', e.target.value)}
          title="开始日期"
        />
        <span>至</span>
        <DateInput
          type="date"
          value={filters.endDate}
          onChange={(e) => handleFilterChange('endDate', e.target.value)}
          title="结束日期"
        />
        <FilterSelect
          value={filters.priority}
          onChange={(e) => handleFilterChange('priority', e.target.value)}
        >
          <option value="">全部优先级</option>
          <option value="high">高</option>
          <option value="medium">中</option>
          <option value="low">低</option>
        </FilterSelect>
        <FilterSelect
          value={filters.type}
          onChange={(e) => handleFilterChange('type', e.target.value)}
        >
          <option value="">全部类型</option>
          <option value="project">项目需求</option>
          <option value="purchase">产品采购</option>
          <option value="service">技术服务</option>
        </FilterSelect>
      </FilterContainer>

      {selectedDemands.length > 0 && (
        <BatchActions>
          <SelectedCount>已选择 {selectedDemands.length} 项</SelectedCount>
          
          {/* 按状态快速选择 */}
          <span style={{ fontSize: '0.85rem', color: '#666' }}>快速选择:</span>
          <SelectionDropdown value={selectFilter} onChange={(e) => handleSelectByStatus(e.target.value)}>
            <option value="all">全部 {demands.length} 条</option>
            <option value="pending">待处理 ({demands.filter(d => d.status === 'pending').length})</option>
            <option value="processing">处理中 ({demands.filter(d => d.status === 'processing').length})</option>
            <option value="completed">已完成 ({demands.filter(d => d.status === 'completed').length})</option>
            <option value="cancelled">已取消 ({demands.filter(d => d.status === 'cancelled').length})</option>
          </SelectionDropdown>
          
          <Button primary onClick={() => handleBatchStatusUpdate('processing')}>批量处理</Button>
          <Button success onClick={() => handleBatchStatusUpdate('completed')}>批量完成</Button>
          <Button warning onClick={() => handleBatchStatusUpdate('cancelled')}>批量取消</Button>
          <Button danger onClick={handleBatchDelete}>🗑️ 批量删除</Button>
          <Button onClick={() => { setSelectedDemands([]); setSelectFilter('all'); }}>取消选择</Button>
          
          {/* 快捷键提示 */}
          <span style={{ fontSize: '0.75rem', color: '#999', marginLeft: '0.5rem' }}>
            快捷键: Ctrl+A 全选 | Delete 删除 | Esc 取消
          </span>
        </BatchActions>
      )}

      <DemandList>
        <SelectAllContainer>
          <Checkbox
            type="checkbox"
            checked={allSelected}
            onChange={handleSelectAll}
          />
          <span>全选</span>
        </SelectAllContainer>
        {demands.length === 0 ? (
          <EmptyState>
            <p style={{ fontSize: '2rem', marginBottom: '1rem' }}>📝</p>
            <p>暂无需求记录</p>
          </EmptyState>
        ) : (
          demands.map((demand) => (
            <DemandItem key={demand._id} selected={selectedDemands.includes(demand._id)}>
              <Checkbox
                type="checkbox"
                checked={selectedDemands.includes(demand._id)}
                onChange={() => handleSelect(demand._id)}
              />
              <DemandContent>
                <DemandHeader>
                  <DemandTitle>
                    {demand.title}
                    <TypeBadge>{getTypeText(demand.type)}</TypeBadge>
                    <PriorityBadge priority={demand.priority}>[{getPriorityText(demand.priority)}]</PriorityBadge>
                  </DemandTitle>
                  <Badge type={demand.status}>{getStatusText(demand.status)}</Badge>
                </DemandHeader>

                <DemandDescription>
                  {demand.description.length > 200
                    ? demand.description.substring(0, 200) + '...'
                    : demand.description}
                </DemandDescription>

                <DemandMeta>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <DemandUser>
                      用户: {demand.userId?.username || (demand.createdByAdmin ? `后台登记（${demand.createdByAdmin}）` : '未知用户')}
                    </DemandUser>
                    {demand.replies && demand.replies.length > 0 && (
                      <span style={{ color: '#007bff' }}>已回复 {demand.replies.length} 条</span>
                    )}
                  </div>
                  <span>{new Date(demand.createdAt).toLocaleString()}</span>
                </DemandMeta>

                {(demand.expectedCommissionRate > 0 || demand.rewardAmount > 0) && (
                  <div style={{ fontSize: '0.8rem', color: '#856404', marginTop: '0.4rem' }}>
                    期望分润 {demand.expectedCommissionRate || 0}%（{demand.commissionBasis === 'profit' ? '按利润' : '按销售额'}）
                    {demand.rewardAmount > 0
                      ? ` · 已核奖励 ¥${demand.rewardAmount}（${demand.rewardRate}%${demand.rewardStatus === 'paid' ? '，已发放' : '，待发放'}）`
                      : ''}
                  </div>
                )}

                <DemandActions>
                  <ActionButton className="primary" onClick={() => handleViewDetail(demand)}>
                    查看详情
                  </ActionButton>
                  <ActionButton className="primary" onClick={() => {
                    setSelectedDemand(demand);
                    setShowReplyModal(true);
                  }}>
                    回复
                  </ActionButton>
                  <ActionButton className="success" onClick={() => openRewardModal(demand)}>
                    成交/奖励
                  </ActionButton>
                  {demand.status === 'pending' && (
                    <ActionButton onClick={() => handleStatusUpdate(demand._id, 'processing')}>
                      开始处理
                    </ActionButton>
                  )}
                  {demand.status === 'processing' && (
                    <ActionButton className="success" onClick={() => handleStatusUpdate(demand._id, 'completed')}>
                      标记完成
                    </ActionButton>
                  )}
                  {demand.status !== 'cancelled' && (
                    <ActionButton className="danger" onClick={() => handleStatusUpdate(demand._id, 'cancelled')}>
                      取消
                    </ActionButton>
                  )}
                  <ActionButton className="danger" onClick={() => handleDelete(demand._id)}>
                    删除
                  </ActionButton>
                </DemandActions>
              </DemandContent>
            </DemandItem>
          ))
        )}
      </DemandList>

      {pagination.totalPages > 1 && (
        <Pagination>
          <PageButton onClick={() => handlePageChange(1)} disabled={pagination.page === 1}>首页</PageButton>
          <PageButton onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1}>上一页</PageButton>
          <span style={{ padding: '0.5rem' }}>第 {pagination.page} / {pagination.totalPages} 页</span>
          <PageButton onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page === pagination.totalPages}>下一页</PageButton>
          <PageButton onClick={() => handlePageChange(pagination.totalPages)} disabled={pagination.page === pagination.totalPages}>末页</PageButton>
        </Pagination>
      )}

      {showDetailModal && selectedDemand && (
        <Modal onClick={() => setShowDetailModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>需求详情</ModalTitle>
              <CloseButton onClick={() => setShowDetailModal(false)}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <FormLabel>标题</FormLabel>
                <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{selectedDemand.title}</div>
              </FormGroup>
              <FormGroup>
                <FormLabel>描述</FormLabel>
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{selectedDemand.description}</div>
              </FormGroup>
              <FormGroup>
                <FormLabel>状态</FormLabel>
                <Badge type={selectedDemand.status}>{getStatusText(selectedDemand.status)}</Badge>
              </FormGroup>
              <FormGroup>
                <FormLabel>类型</FormLabel>
                <div>{getTypeText(selectedDemand.type)}</div>
              </FormGroup>
              <FormGroup>
                <FormLabel>分类专属信息</FormLabel>
                {selectedDemand.details && Object.keys(selectedDemand.details).length > 0 ? (
                  <div>
                    {Object.keys(selectedDemand.details).map((key) => (
                      <div key={key} style={{ fontSize: '0.9rem', color: '#555', marginBottom: '0.25rem' }}>
                        {DETAIL_LABELS[key] || key}：{String(selectedDemand.details[key])}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: '#999' }}>无</div>
                )}
              </FormGroup>
              <FormGroup>
                <FormLabel>优先级</FormLabel>
                <PriorityBadge priority={selectedDemand.priority}>{getPriorityText(selectedDemand.priority)}</PriorityBadge>
              </FormGroup>
              <FormGroup>
                <FormLabel>提交用户</FormLabel>
                <div>
                  {selectedDemand.userId?.username || (selectedDemand.createdByAdmin ? `后台登记（${selectedDemand.createdByAdmin}）` : '未知用户')}
                  {selectedDemand.userId?.email && ` (${selectedDemand.userId.email})`}
                  {selectedDemand.userId?.phone && ` - ${selectedDemand.userId.phone}`}
                </div>
              </FormGroup>
              <FormGroup>
                <FormLabel>提交时间</FormLabel>
                <div>{new Date(selectedDemand.createdAt).toLocaleString()}</div>
              </FormGroup>
              <FormGroup>
                <FormLabel>更新时间</FormLabel>
                <div>{new Date(selectedDemand.updatedAt).toLocaleString()}</div>
              </FormGroup>
              {selectedDemand.replies && selectedDemand.replies.length > 0 && (
                <FormGroup>
                  <FormLabel>回复记录 ({selectedDemand.replies.length})</FormLabel>
                  <ReplyList>
                    {selectedDemand.replies.map((reply, index) => (
                      <ReplyItem key={index}>
                        <ReplyHeader>
                          <ReplyAdmin>{reply.adminName}</ReplyAdmin>
                          <ReplyTime>{new Date(reply.createdAt).toLocaleString()}</ReplyTime>
                        </ReplyHeader>
                        <ReplyContent>{reply.content}</ReplyContent>
                      </ReplyItem>
                    ))}
                  </ReplyList>
                </FormGroup>
              )}
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => setShowDetailModal(false)}>关闭</Button>
              <Button primary onClick={() => {
                setShowDetailModal(false);
                setSelectedDemand(selectedDemand);
                setShowReplyModal(true);
              }}>回复</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {showReplyModal && selectedDemand && (
        <Modal onClick={() => setShowReplyModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>回复需求</ModalTitle>
              <CloseButton onClick={() => setShowReplyModal(false)}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <FormLabel>需求标题</FormLabel>
                <div>{selectedDemand.title}</div>
              </FormGroup>
              <FormGroup>
                <FormLabel>回复内容</FormLabel>
                <FormTextarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="请输入回复内容..."
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => setShowReplyModal(false)}>取消</Button>
              <Button className="primary" onClick={handleReply} disabled={submittingReply}>
                {submittingReply ? '提交中...' : '发送回复'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* 成交与奖励设置模态框 */}
      {showRewardModal && selectedDemand && (
        <Modal onClick={() => !submittingReward && setShowRewardModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>设置成交与奖励</ModalTitle>
              <CloseButton onClick={() => setShowRewardModal(false)}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <FormLabel>需求标题</FormLabel>
                <div>{selectedDemand.title}</div>
              </FormGroup>
              <FormGroup>
                <FormLabel>用户预设期望分润</FormLabel>
                <div style={{ color: '#856404' }}>
                  {selectedDemand.expectedCommissionRate || 0}%（{selectedDemand.commissionBasis === 'profit' ? '按利润' : '按销售额'}）
                </div>
              </FormGroup>
              <FormGroup>
                <FormLabel>分润依据</FormLabel>
                <FilterSelect
                  value={rewardForm.commissionBasis}
                  onChange={(e) => setRewardForm(prev => ({ ...prev, commissionBasis: e.target.value }))}
                >
                  <option value="sales">按销售额</option>
                  <option value="profit">按利润</option>
                </FilterSelect>
              </FormGroup>
              <FormGroup>
                <FormLabel>成交金额（元）</FormLabel>
                <SearchInput
                  type="number"
                  min="0"
                  value={rewardForm.dealAmount}
                  onChange={(e) => setRewardForm(prev => ({ ...prev, dealAmount: e.target.value }))}
                  placeholder="请输入成交金额"
                  style={{ width: '100%' }}
                />
              </FormGroup>
              {rewardForm.commissionBasis === 'profit' && (
                <FormGroup>
                  <FormLabel>利润金额（元）</FormLabel>
                  <SearchInput
                    type="number"
                    min="0"
                    value={rewardForm.profitAmount}
                    onChange={(e) => setRewardForm(prev => ({ ...prev, profitAmount: e.target.value }))}
                    placeholder="请输入利润金额"
                    style={{ width: '100%' }}
                  />
                  <div style={{ fontSize: '0.8rem', color: '#856404', marginTop: '0.4rem', lineHeight: 1.6 }}>
                    按利润分润：我方不公开成本数据，利润金额由我司核算，一切源于信任
                  </div>
                </FormGroup>
              )}
              <FormGroup>
                <FormLabel>奖励比例（%）</FormLabel>
                <SearchInput
                  type="number"
                  min="0"
                  max="100"
                  value={rewardForm.rewardRate}
                  onChange={(e) => setRewardForm(prev => ({ ...prev, rewardRate: e.target.value }))}
                  placeholder="0-100"
                  style={{ width: '100%' }}
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>奖励状态</FormLabel>
                <FilterSelect
                  value={rewardForm.rewardStatus}
                  onChange={(e) => setRewardForm(prev => ({ ...prev, rewardStatus: e.target.value }))}
                >
                  <option value="none">未成交</option>
                  <option value="dealed">已成交（待发奖励）</option>
                  <option value="paid">奖励已发放</option>
                </FilterSelect>
              </FormGroup>
              <FormGroup>
                <FormLabel>备注</FormLabel>
                <FormTextarea
                  value={rewardForm.rewardRemark}
                  onChange={(e) => setRewardForm(prev => ({ ...prev, rewardRemark: e.target.value }))}
                  placeholder="可选，例如奖励发放方式"
                />
              </FormGroup>
              <div style={{ fontSize: '0.875rem', color: '#666' }}>
                {rewardForm.commissionBasis === 'profit'
                  ? <>奖励金额 = 利润金额 × 奖励比例 = ¥{(((Number(rewardForm.profitAmount) || 0) * (Number(rewardForm.rewardRate) || 0)) / 100).toFixed(2)}</>
                  : <>奖励金额 = 成交金额 × 奖励比例 = ¥{(((Number(rewardForm.dealAmount) || 0) * (Number(rewardForm.rewardRate) || 0)) / 100).toFixed(2)}</>}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => setShowRewardModal(false)}>取消</Button>
              <Button primary onClick={handleRewardSubmit} disabled={submittingReward}>
                {submittingReward ? '保存中...' : '保存'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* 登记需求：字段与小程序录入表单一致（分类、专属字段、分润依据） */}
      {showCreateModal && (
        <Modal onClick={() => !submittingCreate && setShowCreateModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>登记需求</ModalTitle>
              <CloseButton onClick={() => setShowCreateModal(false)}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <FormLabel>需求分类</FormLabel>
                <FilterSelect
                  value={createForm.type}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, type: e.target.value, details: {} }))}
                >
                  {typeOptions.map(t => (
                    <option key={t.value} value={t.value}>{t.name}</option>
                  ))}
                </FilterSelect>
                {currentTypeDesc && (
                  <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.4rem', lineHeight: 1.6 }}>
                    {currentTypeDesc}
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <FormLabel>需求标题 *</FormLabel>
                <SearchInput
                  value={createForm.title}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="一句话说明需求"
                  style={{ width: '100%' }}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>需求描述 *</FormLabel>
                <FormTextarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="请描述需求场景、具体要求等"
                />
              </FormGroup>

              {currentTypeFields.map(f => (
                <FormGroup key={f.key}>
                  <FormLabel>{f.label}{f.required ? ' *' : ''}</FormLabel>
                  {f.control === 'select' ? (
                    <FilterSelect
                      value={createForm.details[f.key] || ''}
                      onChange={(e) => handleCreateFieldChange(f.key, e.target.value)}
                    >
                      <option value="">请选择</option>
                      {(f.options || []).map(o => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </FilterSelect>
                  ) : f.control === 'textarea' ? (
                    <FormTextarea
                      value={createForm.details[f.key] || ''}
                      onChange={(e) => handleCreateFieldChange(f.key, e.target.value)}
                      placeholder={f.placeholder}
                    />
                  ) : (
                    <SearchInput
                      type={f.control === 'number' ? 'number' : 'text'}
                      value={createForm.details[f.key] || ''}
                      onChange={(e) => handleCreateFieldChange(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      style={{ width: '100%' }}
                    />
                  )}
                </FormGroup>
              ))}

              <FormGroup>
                <FormLabel>紧急程度</FormLabel>
                <FilterSelect
                  value={createForm.priority}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, priority: e.target.value }))}
                >
                  <option value="low">低</option>
                  <option value="medium">中</option>
                  <option value="high">高</option>
                </FilterSelect>
              </FormGroup>

              <FormGroup>
                <FormLabel>分润依据</FormLabel>
                <FilterSelect
                  value={createForm.commissionBasis}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, commissionBasis: e.target.value }))}
                >
                  {commissionOptions.length > 0
                    ? commissionOptions.map(b => (
                        <option key={b.value} value={b.value}>{b.name}</option>
                      ))
                    : (
                      <>
                        <option value="sales">按销售额</option>
                        <option value="profit">按利润</option>
                      </>
                    )}
                </FilterSelect>
                {createForm.commissionBasis === 'profit' && (
                  <div style={{ fontSize: '0.8rem', color: '#856404', marginTop: '0.4rem', lineHeight: 1.6 }}>
                    按利润分润：我方不公开成本数据，利润金额由我司核算，一切源于信任
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <FormLabel>期望分润比例（%）</FormLabel>
                <SearchInput
                  type="number"
                  min="0"
                  max="100"
                  value={createForm.expectedCommissionRate}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, expectedCommissionRate: e.target.value }))}
                  style={{ width: '100%' }}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>预估金额（元）</FormLabel>
                <SearchInput
                  type="number"
                  min="0"
                  value={createForm.budget}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, budget: e.target.value }))}
                  placeholder="选填，便于评估需求价值"
                  style={{ width: '100%' }}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>联系方式</FormLabel>
                <SearchInput
                  value={createForm.contact}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, contact: e.target.value }))}
                  placeholder="选填，手机号或微信，便于回访"
                  style={{ width: '100%' }}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => setShowCreateModal(false)}>取消</Button>
              <Button primary onClick={handleCreateDemand} disabled={submittingCreate}>
                {submittingCreate ? '提交中...' : '提交'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* 批量删除确认模态框 */}
      {showBatchDeleteModal && (
        <Modal onClick={() => !batchDeleteProgress.deleting && setShowBatchDeleteModal(false)}>
          <ConfirmModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>确认删除</ModalTitle>
              {!batchDeleteProgress.deleting && (
                <CloseButton onClick={() => setShowBatchDeleteModal(false)}>&times;</CloseButton>
              )}
            </ModalHeader>
            <ModalBody>
              <WarningIcon>⚠️</WarningIcon>
              
              {!batchDeleteProgress.deleting ? (
                <>
                  <ConfirmMessage>
                    确定要删除选中的 <strong>{selectedDemands.length}</strong> 条需求吗？
                  </ConfirmMessage>
                  <p style={{ textAlign: 'center', color: '#dc3545', marginBottom: '1rem' }}>
                    此操作不可撤销，删除后数据将无法恢复！
                  </p>
                  
                  <ConfirmDetails>
                    <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#333' }}>
                      即将删除的需求列表：
                    </div>
                    {demands
                      .filter(d => selectedDemands.includes(d._id))
                      .slice(0, 10)
                      .map(d => (
                        <ConfirmDetailItem key={d._id}>
                          <span style={{ flex: 1, wordBreak: 'break-word' }}>
                            {d.title}
                          </span>
                          <Badge type={d.status} style={{ marginLeft: '0.5rem', flexShrink: 0 }}>
                            {getStatusText(d.status)}
                          </Badge>
                        </ConfirmDetailItem>
                      ))}
                    {selectedDemands.length > 10 && (
                      <div style={{ textAlign: 'center', color: '#666', padding: '0.5rem', fontSize: '0.85rem' }}>
                        ...还有 {selectedDemands.length - 10} 条未显示
                      </div>
                    )}
                  </ConfirmDetails>
                </>
              ) : (
                <DeleteProgress>
                  <ConfirmMessage>正在删除...</ConfirmMessage>
                  <ProgressBar>
                    <ProgressFill percent={Math.round((batchDeleteProgress.current / batchDeleteProgress.total) * 100)} />
                  </ProgressBar>
                  <ProgressText>
                    {batchDeleteProgress.current} / {batchDeleteProgress.total} 条已删除
                  </ProgressText>
                </DeleteProgress>
              )}
            </ModalBody>
            <ModalFooter>
              {!batchDeleteProgress.deleting && (
                <>
                  <Button onClick={() => setShowBatchDeleteModal(false)}>取消</Button>
                  <Button danger onClick={confirmBatchDelete}>确认删除</Button>
                </>
              )}
            </ModalFooter>
          </ConfirmModalContent>
        </Modal>
      )}
    </Container>
    </AdminLayout>
  );
};

export default DemandsPage;